import { useTrustNGOSession } from "./useTrustNGOSession";
import { isPlatformSuperAdminEmail } from "@/lib/trustngo-auth";

/**
 * useRole - Hook for role-based access control
 *
 * Provides role information for the current user in the sigmacert tenant
 */
export function useRole() {
  const { session, user } = useTrustNGOSession();
  const isSuperAdmin = isPlatformSuperAdminEmail(user?.email || session?.email);

  const getRole = (tenant: string = 'sigmacert'): string | null => {
    if (tenant === 'sigmacert' && isSuperAdmin) return 'admin';
    if (!session?.roles) return null;
    return session.roles[tenant] || null;
  };

  const hasRole = (role: string, tenant: string = 'sigmacert'): boolean => {
    return getRole(tenant) === role;
  };

  const isAdmin = (): boolean => {
    return hasRole('admin', 'sigmacert');
  };

  const isClient = (): boolean => {
    const role = getRole('sigmacert');
    return role !== null && role !== 'admin';
  };

  const hasTenantAccess = (tenant: string = 'sigmacert'): boolean => {
    if (tenant === 'sigmacert' && isSuperAdmin) return true;
    if (!session?.tenants) return false;
    return session.tenants.includes(tenant);
  };

  return {
    role: getRole(),
    isAdmin: isAdmin(),
    isClient: isClient(),
    hasRole,
    hasTenantAccess,
    session,
    user
  };
}
